import { StarOutlined, StarOutlineOutlined } from '@mui/icons-material'
import { ToggleButton as MuiToggleButton, Tooltip, ToggleButtonProps, styled, Theme } from '@mui/material'
import { FeatureFlag, useFeatureFlag } from 'app/ui/useFeatureFlag'
import clsx from 'clsx'
import { useState } from 'react'
import { useIntl } from 'react-intl'

const ToggleButton = styled(MuiToggleButton)(({ theme }) => ({
  '&.MuiToggleButton-sizeXsmall': {
    width: theme.spacing(2.25),
    height: theme.spacing(2.25),
    paddingX: theme.spacing(0.5),
    minWidth: theme.spacing(2),

    '& > .MuiSvgIcon-root': {
      fontSize: theme.spacing(2),
      minWidth: theme.spacing(2),
      minHeight: theme.spacing(2),
      width: theme.spacing(2),
      height: theme.spacing(2)
    }
  }
}))

interface BookmarkButtonProps {
  onClick?: (e: React.MouseEvent<HTMLElement>, value: boolean) => void
  starred?: boolean
  className?: string
  size?: ToggleButtonProps['size'] | 'xsmall'
  'aria-label'?: string
  tooltip?: string
  backdrop?: 'default' | 'none'
}

function BookmarkButton({
  starred = false,
  size = 'medium',
  className,
  tooltip: customTooltip,
  backdrop = 'default',
  'aria-label': ariaLabel,
  onClick
}: BookmarkButtonProps) {
  const isBookmarksEnabled = useFeatureFlag(FeatureFlag.ConnectionBookmarks)
  const [Icon, setIcon] = useState<React.ElementType>(StarOutlineOutlined)
  const intl = useIntl()
  const tooltip = customTooltip
    ? customTooltip
    : starred
    ? intl.formatMessage({ id: 'bookmarks.bookmarkButton.removeFromStarred' })
    : intl.formatMessage({ id: 'bookmarks.bookmarkButton.addToStarred' })

  if (!isBookmarksEnabled) {
    return null
  }

  return (
    <Tooltip title={tooltip}>
      <ToggleButton
        aria-label={ariaLabel}
        value={'starred'}
        selected={starred}
        className={clsx(className, starred && 'starred', 'bookmark-button')}
        onMouseEnter={() => setIcon(StarOutlined)}
        onMouseLeave={() => setIcon(StarOutlineOutlined)}
        onClick={onClick}
        sx={{
          pointerEvents: onClick ? 'auto' : 'none',
          ...(backdrop === 'none'
            ? {
                '&.MuiToggleButton-root.Mui-selected, &.MuiToggleButton-root:hover': {
                  backgroundColor: 'transparent'
                },
                '&.MuiToggleButton-root.Mui-selected:hover': {
                  color: (theme: Theme) => theme.palette.primary.dark
                }
              }
            : undefined)
        }}
        size={size as ToggleButtonProps['size']}
      >
        {starred ? <StarOutlined /> : <Icon />}
      </ToggleButton>
    </Tooltip>
  )
}

export default BookmarkButton
